import React, { useState } from "react";
import { Text, Platform, Image, Pressable, View } from "react-native";
import * as WebBrowser from "expo-web-browser";
import * as AuthSession from "expo-auth-session";
import * as Google from "expo-auth-session/providers/google";
import { notificationService } from "../services/notification-service";

WebBrowser.maybeCompleteAuthSession();

interface GoogleAuthButtonMobileProps {
  navigation: any;
  RBRef: React.RefObject<any>;
  destination: string;
  onSubmit: (email: string, password: string, logintype: string) => void;
}

const GoogleAuthButtonMobile: React.FC<GoogleAuthButtonMobileProps> = ({
  navigation,
  RBRef,
  destination,
  onSubmit,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // iOS: use expo-auth-session directly (works with iOS client IDs)
  const [iosRequest, iosResponse, iosPromptAsync] = Google.useAuthRequest({
    iosClientId: process.env.REACT_NATIVE_GOOGLE_IOS_CLIENT_ID!,
    androidClientId: process.env.REACT_NATIVE_GOOGLE_ANDROID_CLIENT_ID!,
    responseType: AuthSession.ResponseType.Code,
    scopes: ["email", "profile"],
  });

  React.useEffect(() => {
    if (Platform.OS !== 'android' && iosResponse?.type === "success") {
      handleIosSuccess(iosResponse.authentication?.accessToken);
    }
  }, [iosResponse]);

  const handleIosSuccess = async (token: string | undefined) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        "https://www.googleapis.com/userinfo/v2/me",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const user = await response.json();
      console.log('[GoogleAuth] iOS user info:', JSON.stringify(user, null, 2));
      onSubmit(user?.email, user?.id, 'google');
      RBRef.current?.dismiss();
    } catch (e) {
      console.log('[GoogleAuth] iOS getUserInfo error:', e);
      notificationService.sendNotification(
        "error",
        "Unable to log in using Google currently. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAndroidPress = async () => {
    setIsLoading(true);
    try {
      const authUrl = `${process.env.REACT_NATIVE_OAUTH2_GOOGLE_INIT_URL}/web`;
      const returnUrl = 'meanderapp://oauth-callback';

      console.log('[GoogleAuth] ===== ANDROID AUTH =====');
      console.log('[GoogleAuth] Opening backend auth URL:', authUrl);
      console.log('[GoogleAuth] Listening for return URL:', returnUrl);

      const result = await WebBrowser.openAuthSessionAsync(authUrl, returnUrl);

      console.log('[GoogleAuth] Browser result type:', result.type);
      if (result.type === 'success') {
        console.log('[GoogleAuth] Browser result URL:', (result as any).url);
      }

      if (result.type === 'success' && 'url' in result && result.url) {
        const url = new URL(result.url);
        const email = url.searchParams.get('email');
        const sub = url.searchParams.get('sub');
        const service = url.searchParams.get('service');

        console.log('[GoogleAuth] Parsed from deep link:', { email, sub, service });

        if (email && sub) {
          onSubmit(email, sub, 'google');
          RBRef.current?.dismiss();
        } else {
          console.log('[GoogleAuth] Missing email or sub in callback URL');
          notificationService.sendNotification(
            "error",
            "Google sign-in did not return required information. Please try again."
          );
        }
      } else if (result.type === 'cancel' || result.type === 'dismiss') {
        console.log('[GoogleAuth] User cancelled/dismissed the auth flow');
      } else {
        console.log('[GoogleAuth] Unexpected result:', JSON.stringify(result, null, 2));
      }
    } catch (err) {
      console.log('[GoogleAuth] Android auth error:', err);
      notificationService.sendNotification(
        "error",
        "Unable to log in using Google currently. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePress = () => {
    if (Platform.OS === 'android') {
      handleAndroidPress();
    } else {
      iosPromptAsync();
    }
  };

  const isDisabled = Platform.OS === 'android' ? isLoading : (isLoading || !iosRequest);

  return (
    <Pressable
      disabled={isDisabled}
      onPress={handlePress}
      className="rounded-full"
    >
      <View className="flex-row justify-center items-center px-4 py-3 my-1 bg-white rounded-full">
        <Image
          source={require("../../assets/googleIcon.png")}
          style={{
            width: 24,
            height: 24,
          }}
          resizeMode="contain"
        />
        <Text className="ml-4 text-lg font-medium text-black">
          Continue with Google
        </Text>
      </View>
    </Pressable>
  );
};

export default GoogleAuthButtonMobile;
